import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Trash2,
  Plus,
  ArrowLeft,
  AlertCircle,
  Lock,
  Mail,
  User,
  Users,
} from "lucide-react";

import DashboardLayout from "../../layouts/DashboardLayout";

interface TeamMember {
  id: string;
  name: string;
  email: string;
  grade: string;
  jobRole: string;
}

const GRADES = [
  { value: "A1", label: "A1 – Analyst" },
  { value: "A2", label: "A2 – Senior Analyst" },
  { value: "A3", label: "A3 – Consultant" },
  { value: "B1", label: "B1 – Senior Consultant" },
  { value: "B2", label: "B2 – Manager" },
  { value: "C1", label: "C1 – Senior Manager" },
  { value: "C2", label: "C2 – Director" },
];

const JOB_ROLES = [
  "Software Engineer",
  "Senior Software Engineer",
  "Data Scientist",
  "UX Designer",
  "Product Manager",
  "Cloud Architect",
  "DevOps Engineer",
  "Business Analyst",
  "AI/ML Engineer",
  "Other",
];

const MAX_MEMBERS = 5;

export default function RegistrationForm() {
  const navigate = useNavigate();

  const currentUser = {
    name: "John Doe",
    email: "john.doe@capgemini.com",
  };

  const [teamName, setTeamName] = useState("");
  const [leaderGrade, setLeaderGrade] = useState("");
  const [leaderJobRole, setLeaderJobRole] = useState("");
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const [newMember, setNewMember] = useState<Omit<TeamMember, "id">>({
    name: "",
    email: "",
    grade: "",
    jobRole: "",
  });

  const [memberError, setMemberError] = useState("");
  const [showMemberForm, setShowMemberForm] = useState(false);

  const totalSize = teamMembers.length + 1;

  const validate = () => {
    const errs: string[] = [];
    if (!teamName.trim()) errs.push("Team name is required");
    if (!leaderGrade) errs.push("Team leader grade is required");
    if (!leaderJobRole) errs.push("Team leader job role is required");
    if (teamMembers.length < 2)
      errs.push("At least 2 additional team members are required");
    setErrors(errs);
    return errs.length === 0;
  };

  const handleAddMember = () => {
    if (!newMember.name.trim()) {
      setMemberError("Member name is required");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(newMember.email)) {
      setMemberError("Valid email is required");
      return;
    }
    if (!newMember.email.endsWith("@capgemini.com")) {
      setMemberError("Only Capgemini email addresses are accepted (e.g. example@capgemini.com)");
      return;
    }
    if (!newMember.grade) {
      setMemberError("Member grade is required");
      return;
    }
    if (!newMember.jobRole) {
      setMemberError("Member job role is required");
      return;
    }

    const member: TeamMember = {
      id: Date.now().toString(),
      ...newMember,
    };

    setTeamMembers([...teamMembers, member]);

    setNewMember({ name: "", email: "", grade: "", jobRole: "" });
    setMemberError("");
    setShowMemberForm(false);
  };

  const handleRemoveMember = (id: string) => {
    setTeamMembers(teamMembers.filter((m) => m.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const registrationData = {
        teamName,
        teamLeader: currentUser,
        leaderGrade,
        leaderJobRole,
        teamMembers,
      };

      sessionStorage.setItem(
        "registrationData",
        JSON.stringify(registrationData)
      );

      navigate("/success");
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#f4f6fb] font-['Inter',sans-serif]">
        {/* Top Navigation */}
        <div className="bg-white/90 backdrop-blur-sm border-b border-black/10 shadow px-4 sm:px-6 h-16 flex items-center justify-between sticky top-0 z-50">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#6c757d] hover:text-[#0058AB] text-sm"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Overview</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="size-8 rounded-[10px] bg-gradient-to-b from-[#0058ab] to-[#1db8f2] shadow flex items-center justify-center">
              <svg width="16" height="16" fill="none">
                <path
                  d="M9.33333 1.33333L2 9.33333H8L7.33333 14.6667L14.6667 6.66667H8.66667L9.33333 1.33333Z"
                  stroke="white"
                  strokeWidth="1.33"
                />
              </svg>
            </div>
            <span className="text-[#121a38] text-sm font-semibold hidden sm:inline">
              Innovation Hackathon 2026
            </span>
          </div>
        </div>

        {/* Page Header */}
        <div className="px-6 sm:px-10 lg:px-16 py-8">
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-2xl bg-gradient-to-b from-[#0058ab] to-[#1db8f2] shadow flex items-center justify-center">
                <Users size={28} className="text-white" />
              </div>

              <div>
                <h1 className="text-[#121a38] text-2xl font-bold">
                  Team Registration
                </h1>
                <p className="text-[#6c757d] text-sm">
                  Innovation Hackathon 2026 · March 25–27, 2026
                </p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-[#6c757d] text-xs">Team Size</p>
              <p className="text-[#0058ab] text-lg font-bold">
                {totalSize} / {MAX_MEMBERS}
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Leader Section */}
            <div className="bg-white rounded-2xl border shadow p-6 sm:p-8 mb-6">
              <h2 className="text-[#121a38] text-base font-bold mb-1">
                Team Leader Details
              </h2>
              <p className="text-[#6c757d] text-sm mb-6">
                Name & email auto-filled from SSO.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#adb5bd]" />
                    <input
                      value={currentUser.name}
                      disabled
                      className="w-full pl-10 pr-10 py-3 border rounded-xl bg-[#f8f9fb]"
                    />
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-[#adb5bd]" />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#adb5bd]" />
                    <input
                      value={currentUser.email}
                      disabled
                      className="w-full pl-10 pr-10 py-3 border rounded-xl bg-[#f8f9fb]"
                    />
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-[#adb5bd]" />
                  </div>
                </div>
              </div>

              {/* Grade + Job Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Your Grade<span className="text-red-500">*</span>
                  </label>
                  <select
                    value={leaderGrade}
                    onChange={(e) => setLeaderGrade(e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                  >
                    <option value=""></option>
                    {GRADES.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Your Job Role<span className="text-red-500">*</span>
                  </label>
                  <select
                    value={leaderJobRole}
                    onChange={(e) => setLeaderJobRole(e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl"
                  >
                    <option value=""></option>
                    {JOB_ROLES.map((role) => (
                      <option key={role}>{role}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Team Info */}
            <div className="bg-white rounded-2xl border shadow p-6 sm:p-8 mb-6">
              <h2 className="text-[#121a38] text-base font-bold mb-1">
                Team Information
              </h2>

              <label className="block text-sm font-semibold mb-2">
                Team Name<span className="text-red-500">*</span>
              </label>
              <input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g. Code Crusaders"
                className="w-full px-4 py-3 border rounded-xl"
              />
            </div>

            {/* Members Table */}
            <div className="bg-white rounded-2xl border shadow p-6 sm:p-8 mb-6">
              <div className="flex justify-between mb-4">
                <h2 className="text-base font-bold">Team Members</h2>

                <div className="px-3 py-1 bg-blue-50 rounded-full border text-xs font-semibold text-[#0058ab]">
                  {totalSize}/{MAX_MEMBERS} members
                </div>
              </div>

              {/* Empty */}
              {teamMembers.length === 0 && !showMemberForm && (
                <div className="border rounded-2xl py-10 flex flex-col items-center text-center mb-4">
                  <Users size={40} className="text-gray-300 mb-3" />
                  <p className="font-semibold">No members yet</p>
                  <p className="text-xs text-gray-400">
                    Add at least 2 members to form a team
                  </p>
                </div>
              )}

              {/* Members table */}
              {teamMembers.length > 0 && (
                <div className="border rounded-2xl overflow-hidden mb-4">
                  <div className="bg-[#f8f9fb] px-4 py-3 grid grid-cols-6 text-xs font-semibold text-gray-500 uppercase">
                    <span>#</span>
                    <span>Name</span>
                    <span>Email</span>
                    <span>Grade</span>
                    <span>Role</span>
                    <span>Remove</span>
                  </div>

                  {teamMembers.map((m, i) => (
                    <div
                      key={m.id}
                      className={`grid grid-cols-6 px-4 py-3 items-center text-sm ${
                        i % 2 === 1 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <span className="font-bold">{i + 2}</span>
                      <span className="font-semibold">{m.name}</span>
                      <span className="text-gray-600">{m.email}</span>
                      <span className="text-[#0058ab] bg-blue-50 px-2 py-1 rounded-full text-xs font-semibold">
                        {m.grade}
                      </span>
                      <span className="text-gray-600">{m.jobRole}</span>

                      <button
                        type="button"
                        onClick={() => handleRemoveMember(m.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Member */}
              {showMemberForm ? (
                <div className="border rounded-2xl p-5 mb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Name */}
                    <div>
                      <label className="text-sm font-semibold mb-2 block">
                        Member Name
                      </label>
                      <input
                        value={newMember.name}
                        onChange={(e) =>
                          setNewMember({ ...newMember, name: e.target.value })
                        }
                        className="w-full px-4 py-2.5 border rounded-xl"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="text-sm font-semibold mb-2 block">
                        Member Email
                      </label>
                      <input
                        value={newMember.email}
                        onChange={(e) =>
                          setNewMember({
                            ...newMember,
                            email: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 border rounded-xl"
                      />
                    </div>

                    {/* Grade */}
                    <div>
                      <label className="text-sm font-semibold mb-2 block">
                        Member Grade
                      </label>
                      <select
                        value={newMember.grade}
                        onChange={(e) =>
                          setNewMember({ ...newMember, grade: e.target.value })
                        }
                        className="w-full px-4 py-2.5 border rounded-xl"
                      >
                        <option value=""></option>
                        {GRADES.map((g) => (
                          <option key={g.value} value={g.value}>
                            {g.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Job Role */}
                    <div>
                      <label className="text-sm font-semibold mb-2 block">
                        Member Role
                      </label>
                      <select
                        value={newMember.jobRole}
                        onChange={(e) =>
                          setNewMember({
                            ...newMember,
                            jobRole: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 border rounded-xl"
                      >
                        <option value=""></option>
                        {JOB_ROLES.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {memberError && (
                    <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                      <AlertCircle size={16} /> {memberError}
                    </p>
                  )}

                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowMemberForm(false);
                        setMemberError("");
                        setNewMember({
                          name: "",
                          email: "",
                          grade: "",
                          jobRole: "",
                        });
                      }}
                      className="px-6 py-2.5 rounded-xl border text-gray-600"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleAddMember}
                      className="px-6 py-2.5 rounded-xl bg-[#0058AB] text-white"
                    >
                      Add Member
                    </button>
                  </div>
                </div>
              ) : teamMembers.length < MAX_MEMBERS - 1 ? (
                <button
                  type="button"
                  onClick={() => setShowMemberForm(true)}
                  className="px-5 py-3 rounded-xl bg-[#00A35C] text-white font-semibold"
                >
                  <Plus size={16} className="mr-2 inline" /> Add Team Member
                </button>
              ) : null}
            </div>

            {/* Error Messages */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-red-600" />
                  <div>
                    <p className="font-semibold text-red-900 mb-2">
                      Please fix the following errors:
                    </p>
                    <ul className="text-red-700 text-sm">
                      {errors.map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="bg-white rounded-2xl border shadow p-6 flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="px-8 py-3 rounded-xl border text-gray-600"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="px-8 py-3 rounded-xl bg-[#0058AB] text-white font-semibold shadow"
              >
                Submit Registration
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}